import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineX } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { downloadBlob } from './format';

export default function EmployeeDetailModal({ employee, month: initialMonth, onClose }) {
    const [month, setMonth] = useState(initialMonth);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = useCallback(() => {
        setLoading(true);
        setError('');
        axiosInstance
            .get(`/api/working-time/employee/${employee.userId}`, { params: { month } })
            .then((r) => setData(r.data))
            .catch((e) => setError(e.response?.data?.message || 'Could not load the timeline'))
            .finally(() => setLoading(false));
    }, [employee.userId, month]);

    useEffect(() => { load(); }, [load]);

    const download = (format) => {
        const extension = format === 'pdf' ? 'pdf' : 'xlsx';
        axiosInstance
            .get(`/api/working-time/employee/${employee.userId}/export`, {
                params: { month, format }, responseType: 'blob'
            })
            .then((r) => downloadBlob(
                r.data,
                `working-time-${employee.employeeCode || employee.name}-${month}.${extension}`
            ))
            .catch(() => setError('Could not download the export'));
    };

    const s = data && data.summary;

    return (
        <div className="wt-modal-backdrop" onClick={onClose} role="presentation">
            <div
                className="wt-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label={`${employee.name} working time`}
            >
                <header className="wt-modal-head">
                    <h2>{employee.name} · working time</h2>
                    <button type="button" className="wt-btn" onClick={onClose} aria-label="Close">
                        <HiOutlineX />
                    </button>
                </header>

                <div className="wt-toolbar">
                    <div className="wt-field">
                        <label htmlFor="wt-detail-month">Month</label>
                        <input
                            id="wt-detail-month"
                            type="month"
                            value={month}
                            onChange={(e) => setMonth(e.target.value)}
                        />
                    </div>
                    <button type="button" className="wt-btn primary" onClick={() => download('xlsx')}>
                        Excel
                    </button>
                    <button type="button" className="wt-btn" onClick={() => download('pdf')}>
                        PDF
                    </button>
                </div>

                {error && <div className="wt-error">{error}</div>}

                {s && (
                    <div className="wt-stats">
                        <div className="wt-stat">
                            <div className="label">Days tracked</div>
                            <div className="value">{s.daysTracked}</div>
                        </div>
                        <div className="wt-stat">
                            <div className="label">Active</div>
                            <div className="value">{s.activeHours}</div>
                        </div>
                        <div className="wt-stat">
                            <div className="label">Idle</div>
                            <div className="value">{s.idleHours}</div>
                        </div>
                        <div className="wt-stat">
                            <div
                                className="label"
                                title="Time the agent was locked/off or otherwise not actively working."
                            >
                                Away
                            </div>
                            <div className="value">{s.awayHours}</div>
                        </div>
                        <div className="wt-stat">
                            <div className="label">Total</div>
                            <div className="value">{s.totalHours}</div>
                        </div>
                        <div className="wt-stat">
                            <div className="label">Avg active/day</div>
                            <div className="value">{s.averageActive}</div>
                        </div>
                    </div>
                )}

                <div className="wt-table-wrap">
                    <table className="wt-table">
                        <thead>
                            <tr>
                                <th>Date</th><th>First Active</th><th>Last Active</th>
                                <th>Active</th><th>Idle</th><th>Away</th><th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && <tr><td colSpan={7} className="wt-empty">Loading…</td></tr>}
                            {!loading && !(data && data.timeline && data.timeline.length) && (
                                <tr>
                                    <td colSpan={7} className="wt-empty">
                                        No agent data for this month.
                                    </td>
                                </tr>
                            )}
                            {!loading && data && data.timeline && data.timeline.map((d) => (
                                <tr key={d.qatarDay}>
                                    <td>{d.qatarDay}</td>
                                    <td className="wt-mono">{d.firstActiveTime || '-'}</td>
                                    <td className="wt-mono">{d.lastActiveTime || '-'}</td>
                                    <td className="wt-mono"><strong>{d.activeHours}</strong></td>
                                    <td className="wt-mono">{d.idleHours}</td>
                                    <td className="wt-mono">{d.awayHours}</td>
                                    <td className="wt-mono">{d.totalHours}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
